import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import get from 'lodash/get';
import { selectors as i18nSelectors, translator } from 'mage-i18n';
import { BorderlessDropdown, SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';
import {
  defaultContainerPropTypes,
  formUtils,
  HorizontalWrapper,
  Input,
  payloadUtils,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { validators } from '@/flow/validation';
import { CAPS } from '@/constants';
import {
  BackButton,
  CallToActionButton,
  ContainerTemplate,
  FieldSetWithAddressForm,
} from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';
import { getUsStates, getCountries } from '@/selectors/options';
import { getMerchantAwareTranslator, getMerchantCountryKey } from '@/selectors/merchant';
import { logEvent } from '@/utils/analytics';

import { ANALYTICS_EVENTS } from '../analyticsEvents';
import { CY_COMPANY_DETAILS_CONTAINER_IDS } from '../constants';
import { getDefaultState } from '../selectors';
import { formatAddress, isUSRegionCountry, getTPrefix, isAddressRegionOptional } from '../utils';

export class CompanyDetailsWithoutOperatingAddress extends React.Component {
  constructor(props) {
    super(props);

    this.getBody = this.getBody.bind(this);
    this.onBlurAddressField = this.onBlurAddressField.bind(this);
    this.onChangeAddressField = this.onChangeAddressField.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onInit = this.onInit.bind(this);
    this.setAddress = this.setAddress.bind(this);
    this.prepareFields = this.prepareFields.bind(this);

    const fields = this.prepareFields(props);
    const initialState = { ...fields };

    const { defaultState: { isLookup } = {}, language } = props;

    this.state = {
      ...initialState,
      language,
      isLookup: isLookup || undefined,
      initialState,
    };
  }

  prepareFields({ t, tPrefix, country, defaultState = {}, countries }) {
    const { companyName = '', address = {} } = defaultState;
    const { city = '', postalCode = '', street = '', region = '' } = address;

    return {
      companyDetails: {
        fields: {
          companyName: {
            component: Input,
            size: 1,
            type: 'text',
            label: t(`${tPrefix}.fields.company_name.label`),
            validators: [
              {
                callback: validators.common.isRequired,
                errorMessage: t(`${tPrefix}.fields.company_name.errors.general`),
              },
            ],
            extraProps: {
              ...generateCyElemId(CY_COMPANY_DETAILS_CONTAINER_IDS.INPUT_COMPANY_NAME, window),
            },
            isInitialized: false,
            isValid: false,
            value: companyName,
          },
          country: this.getCountryField(t, tPrefix, country, countries),
          postalCode: {
            component: Input,
            size: 0.5,
            type: 'text',
            label: t(`${tPrefix}.fields.postal_code.label`),
            validators: [
              {
                callback: validators.common.isRequired,
                errorMessage: t(`${tPrefix}.fields.postal_code.errors.general`),
              },
            ],
            isInitialized: false,
            isValid: false,
            value: postalCode,
          },
          street: {
            component: Input,
            size: 0.5,
            type: 'text',
            label: t(`${tPrefix}.fields.street.label`),
            validators: [
              {
                callback: validators.common.isRequired,
                errorMessage: t(`${tPrefix}.fields.street.errors.general`),
              },
            ],
            isInitialized: false,
            isValid: false,
            value: street,
          },
          city: {
            component: Input,
            size: 0.5,
            label: t(`${tPrefix}.fields.city.label`),
            validators: [
              {
                callback: validators.common.isRequired,
                errorMessage: t(`${tPrefix}.fields.city.errors.general`),
              },
            ],
            isInitialized: false,
            isValid: false,
            value: city,
          },
          region: this.getRegionForMainAddress(t, tPrefix, country, region),
        },
      },
    };
  }

  getCountryField(t, tPrefix, country, countries) {
    if (isUSRegionCountry(country)) {
      return {
        component: Input,
        size: 0.5,
        label: t(`${tPrefix}.fields.country.label`),
        extraProps: {
          disabled: true,
        },
        value: country,
      };
    }
    return {
      component: BorderlessDropdown,
      size: 0.5,
      label: `${tPrefix}.fields.country.label`,
      validators: [
        {
          callback: validators.common.isRequired,
          errorMessage: `${tPrefix}.fields.country.error_message`,
        },
      ],
      extraProps: {
        disabled: true,
        options: countries,
      },
      isInitialized: true,
      isValid: true,
      value: country,
    };
  }

  getRegionForMainAddress(t, tPrefix, country, value) {
    const { states } = this.props;
    if (!isAddressRegionOptional) {
      return {
        component: BorderlessDropdown,
        size: 1,
        label: t(`${tPrefix}.fields.state_of_incorporation.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.state_of_incorporation.errors.general`),
          },
        ],
        extraProps: {
          options: states,
          ...generateCyElemId(CY_COMPANY_DETAILS_CONTAINER_IDS.INPUT_ADDRESS_REGION, window),
        },
        isInitialized: false,
        isValid: false,
        value,
      };
    }
    return {
      component: Input,
      size: 1,
      label: t(`${tPrefix}.fields.region.label`),
      isOptional: true,
      validators: [
        {
          callback: (region, { country }) => country !== 'US' || region !== 'PR',
          errorMessage: t(`${tPrefix}.fields.region.errors.puerto_rico`),
          options: { country },
        },
      ],
      extraProps: {
        ...generateCyElemId(CY_COMPANY_DETAILS_CONTAINER_IDS.INPUT_ADDRESS_REGION, window),
      },
      isInitialized: true,
      isValid: true,
      value,
    };
  }

  static updateCompanyDetailsTranslation(key, props) {
    const { t, tPrefix, country, countries } = props;
    const getRegionAndCountryLabels = () => {
      if (!isAddressRegionOptional) {
        return {
          country: {
            label: t(`${tPrefix}.fields.country.label`),
            extraProps: {
              disabled: true,
            },
            value: country,
          },
          region: {
            label: t(`${tPrefix}.fields.state_of_incorporation.label`),
            validators: [
              {
                callback: validators.common.isRequired,
                errorMessage: t(`${tPrefix}.fields.state_of_incorporation.errors.general`),
              },
            ],
          },
        };
      }
      return {
        country: {
          label: `${tPrefix}.fields.country.label`,
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: `${tPrefix}.fields.country.error_message`,
            },
          ],
          extraProps: {
            options: countries,
          },
        },
        region: { label: t(`${tPrefix}.fields.region.label`) },
      };
    };

    const fieldTranslationsMap = {
      companyName: {
        label: `${tPrefix}.fields.company_name.label`,
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: `${tPrefix}.fields.company_name.errors.general`,
          },
        ],
      },
      postalCode: {
        label: `${tPrefix}.fields.postal_code.label`,
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: `${tPrefix}.fields.postal_code.errors.general`,
          },
        ],
      },
      street: {
        label: `${tPrefix}.fields.street.label`,
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: `${tPrefix}.fields.street.errors.general`,
          },
        ],
      },
      city: {
        label: `${tPrefix}.fields.city.label`,
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: `${tPrefix}.fields.city.errors.general`,
          },
        ],
      },
    };
    Object.assign(fieldTranslationsMap, getRegionAndCountryLabels());

    return formUtils.updateTranslatedFields(fieldTranslationsMap, key, t);
  }

  static getDerivedStateFromProps(props, state) {
    const { language } = props;
    const companyDetailsFields = get(state, 'companyDetails.fields', {});
    if (state.language !== props.language) {
      const newState = {
        ...state,
        language,
        companyDetails: formUtils.initFormFromState({
          ...state.companyDetails,
          fields: {
            ...companyDetailsFields,
            ...Object.keys(companyDetailsFields).reduce((acc, field) => {
              acc[field] = {
                ...companyDetailsFields[field],
                ...CompanyDetailsWithoutOperatingAddress.updateCompanyDetailsTranslation(
                  field,
                  props,
                ),
              };
              return acc;
            }, {}),
          },
        }),
      };

      return { ...newState, initialState: state.initialState };
    }

    return null;
  }

  onInit(kind) {
    return fields => {
      const initializedFields = formUtils.initForm(fields);
      this.setState({ [kind]: { fields: initializedFields } });
    };
  }

  setAddress(companyDetails) {
    this.setState({ companyDetails });
  }

  onChangeAddressField(key, val) {
    formUtils.onChange(key, val, this.state.companyDetails, this.setAddress);
  }

  onBlurAddressField(key) {
    formUtils.onBlur(key, this.state.companyDetails, this.setAddress);
  }

  getBody({ companyDetails, isLookup }) {
    const { companyName, ...address } = formUtils.getMappedValues(companyDetails.fields);
    return { companyName, address, isLookup };
  }

  onNext() {
    const { companyDetails } = this.state;

    const initializedCompanyDetails = formUtils.initializeFields(companyDetails.fields);

    this.setState({
      ...this.state,
      companyDetails: {
        ...this.state.companyDetails,
        fields: initializedCompanyDetails,
      },
    });

    formUtils.callToActionOnNextWrapper(
      [this.state.companyDetails.fields],
      () => {
        this.props.onNext(
          payloadUtils.getMerchantCapabilityPayload(this.props.mid, this.state, this.getBody),
        );
      },
      formUtils.isFormInitializedAndValid(this.state.companyDetails.fields),
    );
  }

  render() {
    const {
      defaultState: { isLookup } = {},
      error,
      hasBack,
      isDisabled,
      isLoading,
      onBack,
      t,
      tPrefix,
      tMerchantAware,
    } = this.props;
    const { companyDetails } = this.state;

    const backButton = (
      <>
        <BackButton tabIndex={11} onClick={onBack} disabled={isDisabled}>
          {t(`${tPrefix}.buttons.back`)}
        </BackButton>
        <SpacerHorizontal spaceToken="space/300" />
      </>
    );

    return (
      <ContainerTemplate
        title={tMerchantAware(`${tPrefix}.title`)}
        subtitle={tMerchantAware(`${tPrefix}.subtitle`)}
        error={error}
        info={isLookup ? t(`${tPrefix}.messages.lookup`) : undefined}
      >
        <FieldSetWithAddressForm
          isBorderless
          isDisabled={isDisabled}
          tabIndexStart={1}
          name="company-details-uk"
          fields={companyDetails.fields}
          onBlur={this.onBlurAddressField}
          onChange={this.onChangeAddressField}
          onInit={this.onInit('companyDetails')}
          {...generateCyElemId(CY_COMPANY_DETAILS_CONTAINER_IDS.COMPANY_DETAILS_FIELDS, window)}
        />
        <SpacerVertical spaceToken="space/600" />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && backButton}
          <CallToActionButton
            tabIndex={10}
            onClick={this.onNext}
            disabled={isDisabled}
            loading={isLoading}
            {...generateCyElemId(CY_COMPANY_DETAILS_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

CompanyDetailsWithoutOperatingAddress.propTypes = {
  ...defaultContainerPropTypes,
  country: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  countries: PropTypes.array,
  defaultState: PropTypes.shape({
    address: PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      postalCode: PropTypes.string,
      region: PropTypes.string,
      street: PropTypes.string,
    }),
    companyName: PropTypes.string,
  }),
  states: PropTypes.array,
};

const WrappedComponent = compose(
  connect(state => ({
    t: translator(state),
    tMerchantAware: getMerchantAwareTranslator(state),
    language: i18nSelectors.language(state),
    countries: getCountries(state),
    states: getUsStates(state),
    defaultState: getDefaultState(state),
    tPrefix: getTPrefix(getMerchantCountryKey(state)),
  })),
  withCapabilityBeforeNext({ cap: CAPS.V2_MERCHANTS_BUSINESS_DETAILS_PATCH }, ({ payload }) => {
    const { companyName, address } = payload.body;

    const fields = {
      companyName,
      address: formatAddress(address),
    };

    logEvent(ANALYTICS_EVENTS.companyDetails, fields);
  }),
)(CompanyDetailsWithoutOperatingAddress);

WrappedComponent.onboardingTypes = '*';
WrappedComponent.deploymentRegionsAndCountries = {
  eu: ['fallback'],
  us: ['fallback'],
  ap: ['GB', 'FR'],
};

WrappedComponent.fieldsToFill = [
  {
    fieldName: 'businessDetails',
    fields: {
      companyName: {},
      'address.city': {},
      'address.country': {},
      'address.postalCode': {},
      'address.street': {},
      'address.region': { optional: isAddressRegionOptional },
    },
  },
];

export default WrappedComponent;
